// NOTE: axios is an ESM package in some environments. Importing it at module
// initialization time can cause Jest to try to parse ESM in node_modules and
// fail ("Cannot use import statement outside a module"). To avoid this,
// lazily require axios only when an API method is actually invoked.

// API Configuration
const getApiBaseUrl = () => {
  // Check environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000/api';
    } else if (hostname.includes('dev.planmysemester.xyz')) {
      return 'https://devapi.planmysemester.xyz/api';
    }
  }
  
  // Default to production
  return 'https://api.planmysemester.xyz/api';
};

const API_BASE_URL = getApiBaseUrl();

// Lazy axios proxy: creates an axios instance on first access to avoid
// importing axios at test/runtime initialization time.
const api = (() => {
  let instance = null;

  const getInstance = () => {
    if (!instance) {
      // require used intentionally to avoid top-level ESM import
      // in environments where axios is provided as ESM.
      // eslint-disable-next-line global-require
      const axios = require('axios');
      instance = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      });
    }
    return instance;
  };

  // Proxy that forwards method calls to the axios instance
  return new Proxy({}, {
    get(_, prop) {
      const inst = getInstance();
      const value = inst[prop];
      if (typeof value === 'function') return value.bind(inst);
      return value;
    }
  });
})();

export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: `${API_BASE_URL}/auth/login`,
  REGISTER: `${API_BASE_URL}/auth/register`,
  LOGOUT: `${API_BASE_URL}/auth/logout`,
  PROFILE: `${API_BASE_URL}/auth/profile`,
  CHANGE_PASSWORD: `${API_BASE_URL}/auth/change-password`,
  DELETE_ACCOUNT: `${API_BASE_URL}/auth/delete-account`,
  VERIFY: `${API_BASE_URL}/auth/verify`,
  VERIFY_TOKEN: `${API_BASE_URL}/auth/verify-token`,
  
  // 2FA endpoints
  SETUP_2FA: `${API_BASE_URL}/auth/setup-2fa`,
  VERIFY_2FA: `${API_BASE_URL}/auth/verify-2fa`,
  VERIFY_2FA_LOGIN: `${API_BASE_URL}/auth/verify-2fa-login`,
  DISABLE_2FA: `${API_BASE_URL}/auth/disable-2fa`,
  RESET_PASSWORD_2FA: `${API_BASE_URL}/auth/reset-password-2fa`,
  
  // Settings endpoints
  SETTINGS: `${API_BASE_URL}/settings`,
  STUDY_TRACKING: `${API_BASE_URL}/settings/study`,
  
  // Course endpoints
  COURSES: `${API_BASE_URL}/courses`,
  COURSE_BY_ID: (id) => `${API_BASE_URL}/courses/${id}`,
  
  // Assignment endpoints
  ASSIGNMENTS: `${API_BASE_URL}/assignments`,
  ASSIGNMENTS_GRADED: `${API_BASE_URL}/assignments/graded`,
  ASSIGNMENT_BY_ID: (id) => `${API_BASE_URL}/assignments/${id}`,
  
  // Analytics endpoints
  ANALYTICS: `${API_BASE_URL}/analytics`,
  
  // Admin endpoints
  ADMIN_USERS: `${API_BASE_URL}/admin/users`,
  ADMIN_USER_BY_ID: (id) => `${API_BASE_URL}/admin/users/${id}`,
  ADMIN_USER_COURSES: (id) => `${API_BASE_URL}/admin/users/${id}/courses`,
  ADMIN_USER_ASSIGNMENTS: (id) => `${API_BASE_URL}/admin/users/${id}/assignments`,
  ADMIN_USER_ROLE: (id) => `${API_BASE_URL}/admin/users/${id}/role`,
  ADMIN_USER_SUBSCRIPTION: (id) => `${API_BASE_URL}/admin/users/${id}/subscription`,
  ADMIN_RESET_TOKENS: (id) => `${API_BASE_URL}/admin/users/${id}/reset-tokens`,
  
  // Public endpoints
  PUBLIC_STATS: `${API_BASE_URL}/public/stats`,
  PUBLIC_GEO: `${API_BASE_URL}/public/geo`,
  
  // Subscription endpoints
  SUBSCRIPTION_PLANS: `${API_BASE_URL}/subscription/plans`,
  SUBSCRIPTION_UPLOAD_LIMIT: `${API_BASE_URL}/subscription/upload-limit`,
  SUBSCRIPTION_PROMOS: `${API_BASE_URL}/subscription/promos`,
  INVOICES: `${API_BASE_URL}/subscription/invoices`,
  STUDY_LEADERBOARD: `${API_BASE_URL}/settings/study/leaderboard`,
  
  // Subscription endpoints
  CANCEL_SUBSCRIPTION: `${API_BASE_URL}/subscription/cancel-subscription`,
  REACTIVATE_SUBSCRIPTION: `${API_BASE_URL}/subscription/reactivate-subscription`,
  
  // Support endpoints
  SUPPORT_TICKET: `${API_BASE_URL}/support/ticket`,
  SUPPORT_TICKETS: `${API_BASE_URL}/support/tickets`,
  SUPPORT_TICKET_BY_ID: (id) => `${API_BASE_URL}/support/ticket/${id}`,
  SUPPORT_TICKET_REPLY: (id) => `${API_BASE_URL}/support/ticket/${id}/reply`,
  SUPPORT_TICKET_STATUS: (id) => `${API_BASE_URL}/support/ticket/${id}/status`,
  SUPPORT_TICKET_CLAIM: (id) => `${API_BASE_URL}/support/ticket/${id}/claim`,
  SUPPORT_TICKET_UNCLAIM: (id) => `${API_BASE_URL}/support/ticket/${id}/unclaim`,
  SUPPORT_ADMIN_TICKETS: `${API_BASE_URL}/support/admin/tickets`,
  SUPPORT_ADMIN_STATS: `${API_BASE_URL}/support/admin/stats`,

  // AI endpoints
  AI_USAGE: `${API_BASE_URL}/ai/usage`,
  AI_GENERATE: `${API_BASE_URL}/ai/generate`,
  AI_RESET_TOKENS: `${API_BASE_URL}/ai/reset-tokens`,
  AI_CREATE_SUBSCRIPTION: `${API_BASE_URL}/ai/create-subscription`,
  AI_SUBSCRIBE: `${API_BASE_URL}/ai/create-subscription`,
  AI_PLANS: `${API_BASE_URL}/ai/plans`,
  AI_COURSE_LEARNING_SUMMARY: (courseId) => `${API_BASE_URL}/ai/course-learning-summary/${courseId}`,

  // Flashcard endpoints
  FLASHCARDS: `${API_BASE_URL}/flashcards`,
  FLASHCARD_DECKS: `${API_BASE_URL}/flashcards/decks`,
  FLASHCARD_DECKS_IMPORT_ANKI: `${API_BASE_URL}/flashcards/decks/import/anki`,
  FLASHCARD_DECK_BY_ID: (deckId) => `${API_BASE_URL}/flashcards/decks/${deckId}`,
  FLASHCARD_PROGRESS: (courseId) => `${API_BASE_URL}/flashcards/progress/${courseId}`,
  FLASHCARD_RATE: `${API_BASE_URL}/flashcards/rate`,
  FLASHCARD_RESET: (courseId) => `${API_BASE_URL}/flashcards/reset/${courseId}`,
};

export { API_BASE_URL };
export default api;
